import React from 'react';
import ParticlesBG from '../../Shared/ParticlesBG';
import '../../Assets/Stylesheets/Pages/Legal/Legal.scss';

export default function CookiePolicy() {
  const lastUpdated = "January 20, 2026";
  const companyName = "TRND";
  const companyEmail = "privacy@trnd.co";

  return (
    <div className="legal-page">
      <ParticlesBG />
      <div className="legal-content">
        <h1>Cookie Policy</h1>
        <p className="last-updated">Last Updated: {lastUpdated}</p>

        <section className="legal-section">
          <h2>1. What Are Cookies?</h2>
          <p>
            Cookies are small text files that are placed on your computer or mobile device 
            when you visit a website. Cookies are widely used by website owners to make their 
            websites work, or to work more efficiently, as well as to provide reporting 
            information.
          </p>
          <p>
            Cookies set by the website owner (in this case, {companyName}) are called 
            "first-party cookies." Cookies set by parties other than the website owner are 
            called "third-party cookies." Third-party cookies enable third-party features or 
            functionality to be provided on or through the website.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Why We Use Cookies</h2>
          <p>We use cookies for several purposes:</p>
          <ul>
            <li><strong>Essential:</strong> To make our website work properly</li>
            <li><strong>Performance:</strong> To analyze how visitors use our website</li>
            <li><strong>Functionality:</strong> To remember your preferences and settings</li>
            <li><strong>Marketing:</strong> To deliver relevant advertisements (with your consent)</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Types of Cookies We Use</h2>
          
          <h3>3.1 Essential Cookies</h3>
          <p>
            These cookies are strictly necessary for the website to function. They cannot be 
            switched off in our systems. They are usually only set in response to actions made 
            by you which amount to a request for services.
          </p>
          <table>
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Purpose</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>sb-*-auth-token</td>
                <td>Supabase authentication session</td>
                <td>Session / 1 week</td>
              </tr>
              <tr>
                <td>trnd_cookie_consent</td>
                <td>Stores your cookie consent preferences</td>
                <td>1 year</td>
              </tr>
            </tbody>
          </table>

          <h3>3.2 Analytics/Performance Cookies</h3>
          <p>
            These cookies allow us to count visits and traffic sources so we can measure 
            and improve the performance of our site.
          </p>
          <table>
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Provider</th>
                <th>Purpose</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>_ga, _ga_*</td>
                <td>Google Analytics</td>
                <td>Distinguishes users and tracks site usage</td>
                <td>2 years</td>
              </tr>
              <tr>
                <td>_gid</td>
                <td>Google Analytics</td>
                <td>Distinguishes users</td>
                <td>24 hours</td>
              </tr>
            </tbody>
          </table>

          <h3>3.3 Functionality Cookies</h3>
          <p>
            These cookies enable the website to provide enhanced functionality and 
            personalization.
          </p>
          <table>
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Purpose</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>mg_guest_prompt_count</td>
                <td>Tracks guest usage of Marketing Genius</td>
                <td>Session</td>
              </tr>
              <tr>
                <td>mg_has_seen_signin_modal</td>
                <td>Prevents sign-in modal from showing repeatedly</td>
                <td>Session</td>
              </tr>
              <tr>
                <td>currency_preference</td>
                <td>Stores your preferred currency (CAD/USD)</td>
                <td>1 year</td>
              </tr>
            </tbody>
          </table>

          <h3>3.4 Third-Party Cookies</h3>
          <p>
            These cookies are set by our third-party service providers:
          </p>
          <table>
            <thead>
              <tr>
                <th>Provider</th>
                <th>Purpose</th>
                <th>More Information</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Supabase</td>
                <td>Authentication and database services</td>
                <td><a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></td>
              </tr>
              <tr>
                <td>Zoho</td>
                <td>Payment processing and billing</td>
                <td><a href="https://www.zoho.com/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a></td>
              </tr>
              <tr>
                <td>Vercel</td>
                <td>Website hosting and performance</td>
                <td><a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="legal-section">
          <h2>4. Local Storage</h2>
          <p>
            In addition to cookies, we also use local storage to store certain information 
            on your device:
          </p>
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>react-use-cart</td>
                <td>Stores shopping cart contents</td>
              </tr>
              <tr>
                <td>mg_*</td>
                <td>Marketing Genius session data</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="legal-section">
          <h2>5. Managing Cookies</h2>
          
          <h3>5.1 Cookie Consent</h3>
          <p>
            When you first visit our website, you will be shown a cookie banner that allows 
            you to accept or decline non-essential cookies. You can change your preferences 
            at any time by clicking the "Cookie Settings" link in the footer.
          </p>

          <h3>5.2 Browser Settings</h3>
          <p>
            Most web browsers allow you to control cookies through their settings. You can 
            usually find these settings in the "Options" or "Preferences" menu of your browser. 
            Here are links to cookie settings for common browsers:
          </p>
          <ul>
            <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
            <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
            <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer">Apple Safari</a></li>
            <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
          </ul>

          <h3>5.3 Consequences of Disabling Cookies</h3>
          <p>
            If you choose to disable cookies, some features of our website may not function 
            properly:
          </p>
          <ul>
            <li>You may not be able to log in or stay logged in</li>
            <li>Your shopping cart may not work correctly</li>
            <li>Your preferences may not be saved</li>
            <li>Some interactive features may be unavailable</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Do Not Track</h2>
          <p>
            Some browsers have a "Do Not Track" feature that signals to websites that you 
            do not want to be tracked. Currently, there is no industry standard for how 
            companies should respond to Do Not Track signals. We do not currently respond 
            to Do Not Track signals, but we respect your privacy choices as described in 
            this policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Updates to This Policy</h2>
          <p>
            We may update this Cookie Policy from time to time to reflect changes in 
            technology, legislation, or our data practices. When we make changes, we will 
            update the "Last Updated" date at the top of this policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Contact Us</h2>
          <p>
            If you have questions about our use of cookies, please contact us:
          </p>
          <ul className="contact-info">
            <li><strong>Email:</strong> {companyEmail}</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
